import { useState, useEffect } from 'react';
import api from '../services/api';
import { Search, Plus, Tags, X, Save, Trash2, Clock, Package, Sparkles, CheckCircle2, AlertCircle, Calendar } from 'lucide-react';

export default function Promotions() {
  const [promos, setPromos] = useState([]);
  const [products, setProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: 'HAPPY_HOUR',
    config: {
      productIds: [],
      discount: 10,
      startTime: '16:00',
      endTime: '19:00',
      days: [1, 2, 3, 4, 5],
      buyQty: 2,
      getQty: 1
    }
  });

  const loadData = async () => {
    try {
      const [pRes, prodRes] = await Promise.all([
        api.get('/promotions'),
        api.get('/products')
      ]);
      setPromos(pRes.data);
      setProducts(prodRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await api.post('/promotions', formData);
      setIsModalOpen(false);
      loadData();
    } catch (err) {
      alert('Error saving promotion');
    }
  };

  const toggleStatus = async (id) => {
    await api.patch(`/promotions/${id}/toggle`);
    loadData();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this promotion?')) {
      await api.delete(`/promotions/${id}`);
      loadData();
    }
  };

  return (
    <div className="space-y-8 animate-fade-in-up pb-10 max-w-7xl mx-auto">
      <div className="flex justify-between items-end mb-4">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight flex items-center gap-3">
            <Sparkles size={32} className="text-brand" /> Promotions & Bundles
          </h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-xs">Drive sales with combos, happy hours, and BOGO deals.</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="bg-brand hover:bg-brand-light text-white px-6 py-3 rounded-2xl shadow-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all hover:-translate-y-0.5">
          <Plus size={18} /> Launch New Promo
        </button>
      </div>

      {promos.length === 0 ? (
        <div className="bg-theme-card/50 border border-theme border-dashed rounded-[3rem] p-20 flex flex-col items-center text-center">
          <div className="w-24 h-24 bg-theme-main/10 rounded-full flex items-center justify-center mb-6">
            <Sparkles size={48} className="text-theme-muted opacity-50" />
          </div>
          <h3 className="text-2xl font-black text-theme-main uppercase tracking-tight mb-2">No Active Campaigns</h3>
          <p className="text-theme-muted max-w-sm font-medium">Boost your sales by launching Happy Hours, BOGO deals, or Bundle offers for your customers.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {promos.map(promo => (
            <div key={promo.id} className={`bg-theme-card rounded-3xl border shadow-soft p-6 relative overflow-hidden flex flex-col transition-all hover:scale-[1.02] ${!promo.active ? 'opacity-60 grayscale' : 'border-theme'}`}>
              <div className={`absolute top-0 right-0 px-4 py-1 text-[10px] font-black uppercase tracking-widest rounded-bl-xl ${
                promo.type === 'HAPPY_HOUR' ? 'bg-amber-500 text-white' :
                promo.type === 'BUNDLE' ? 'bg-indigo-500 text-white' : 'bg-rose-500 text-white'
              }`}>
                {promo.type.replace('_', ' ')}
              </div>

              <div className="flex-1">
                <h3 className="text-lg font-black text-theme-main uppercase tracking-tight mb-2">{promo.name}</h3>
                
                <div className="space-y-3 mt-4">
                  {promo.type === 'HAPPY_HOUR' && (
                    <div className="flex items-center gap-2 text-xs text-theme-muted font-bold">
                      <Clock size={14} className="text-amber-500" />
                      {promo.config.startTime} - {promo.config.endTime} • {promo.config.discount}% OFF
                    </div>
                  )}
                  {promo.type === 'BUNDLE' && (
                    <div className="flex items-center gap-2 text-xs text-theme-muted font-bold">
                      <Package size={14} className="text-indigo-500" />
                      Combo Discount: {promo.config.discount}%
                    </div>
                  )}
                  {promo.type === 'BOGO' && (
                    <div className="flex items-center gap-2 text-xs text-theme-muted font-bold">
                      <Tags size={14} className="text-rose-500" />
                      Buy {promo.config.buyQty} Get {promo.config.getQty} Free
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-[10px] text-theme-muted font-black uppercase tracking-widest">
                    <Calendar size={14} />
                    {promo.config.days?.length === 7 ? 'All Week' : `${promo.config.days?.length} Days/Week`}
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-theme flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => toggleStatus(promo.id)}
                    className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors ${promo.active ? 'bg-brand' : 'bg-theme-muted'}`}
                  >
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${promo.active ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  <span className="text-[10px] font-black uppercase text-theme-muted">{promo.active ? 'Live' : 'Paused'}</span>
                </div>
                <button onClick={() => handleDelete(promo.id)} className="p-2 text-theme-muted hover:text-rose-500 hover:bg-rose-500/10 rounded-xl transition-all">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-md flex items-start justify-center pt-24 z-50 animate-fade-in p-4">
          <div className="bg-theme-card rounded-3xl shadow-2xl w-full max-w-lg border border-theme flex flex-col mb-10">
            <div className="px-8 py-6 border-b border-theme bg-theme-main/10 flex justify-between items-center">
              <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">Create Campaign</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-theme-muted hover:text-theme-main p-1.5 rounded-xl transition-colors">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSave} className="p-8 space-y-6 overflow-y-auto max-h-[70vh] custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Campaign Name</label>
                  <input required type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full bg-theme-main border border-theme rounded-2xl py-3 px-5 text-theme-main font-bold outline-none focus:ring-2 focus:ring-brand" placeholder="e.g. Summer Happy Hour" />
                </div>

                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Promotion Type</label>
                  <select 
                    value={formData.type} 
                    onChange={e => setFormData({...formData, type: e.target.value})}
                    className="w-full bg-theme-main border border-theme rounded-2xl py-3 px-5 text-theme-main font-bold outline-none focus:ring-2 focus:ring-brand"
                  >
                    <option value="HAPPY_HOUR">Happy Hour</option>
                    <option value="BUNDLE">Bundle / Combo</option>
                    <option value="BOGO">Buy-X-Get-Y (BOGO)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Discount (%)</label>
                  <input type="number" value={formData.config.discount} onChange={e => setFormData({...formData, config: {...formData.config, discount: parseInt(e.target.value)}})} className="w-full bg-theme-main border border-theme rounded-2xl py-3 px-5 text-theme-main font-black outline-none focus:ring-2 focus:ring-brand" />
                </div>

                {formData.type === 'HAPPY_HOUR' && (
                  <>
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Start Time</label>
                      <input type="time" value={formData.config.startTime} onChange={e => setFormData({...formData, config: {...formData.config, startTime: e.target.value}})} className="w-full bg-theme-main border border-theme rounded-2xl py-3 px-5 text-theme-main font-bold" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">End Time</label>
                      <input type="time" value={formData.config.endTime} onChange={e => setFormData({...formData, config: {...formData.config, endTime: e.target.value}})} className="w-full bg-theme-main border border-theme rounded-2xl py-3 px-5 text-theme-main font-bold" />
                    </div>
                  </>
                )}
              </div>

              <div>
                <label className="block text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-2">Target Products</label>
                <div className="bg-theme-main rounded-2xl border border-theme p-4 max-h-48 overflow-y-auto custom-scrollbar grid grid-cols-1 gap-2">
                  {products.map(p => (
                    <label key={p.id} className="flex items-center gap-3 p-2 hover:bg-theme-card rounded-xl transition-all cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={formData.config.productIds.includes(p.id)}
                        onChange={(e) => {
                          const ids = e.target.checked 
                            ? [...formData.config.productIds, p.id]
                            : formData.config.productIds.filter(id => id !== p.id);
                          setFormData({...formData, config: {...formData.config, productIds: ids}});
                        }}
                        className="w-4 h-4 rounded border-theme text-brand focus:ring-brand" 
                      />
                      <span className="text-xs font-bold text-theme-main">{p.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="pt-6 border-t border-theme">
                <button type="submit" className="w-full py-4 bg-brand hover:bg-brand-light text-white rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl shadow-brand/20 transition-all active:scale-95">
                  Activate Campaign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
